import React, { useEffect } from 'react'
import { Phone, Mail, MapPin, ShieldCheck, Clock, Award } from 'lucide-react'
import ContactForm from '../components/ContactForm'

export default function ContactPage() {
  // SEO Focus
  useEffect(() => {
    document.title = 'Kontakt | Malerfirma Villy Poulsen ApS | Få et uforpligtende tilbud'
    const metaDescriptionTag = document.querySelector('meta[name="description"]')
    if (metaDescriptionTag) {
      metaDescriptionTag.setAttribute(
        'content',
        'Kontakt Malerfirma Villy Poulsen ApS i Spentrup nær Randers og Hobro for et uforpligtende tilbud på malerarbejde. Ring på mobil 40 63 76 70 eller send en mail.'
      )
    }
  }, [])

  return (
    <div className="bg-white">
      {/* Page Header */}
      <section className="relative bg-[#20252C] text-white py-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/assets/bbcf0f14-2bcd-4cd4-98b4-2d62ce00d60f/d142c128-4133-4307-a6d3-d23c7b9a7ce7.jpeg"
            alt="Kontakt Malerfirma Villy Poulsen ApS"
            className="w-full h-full object-cover opacity-45"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-950/80 via-gray-900/50 to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black mt-4">
            Kontakt Malerfirma Villy Poulsen ApS
          </h1>
          <p className="text-gray-300 text-sm sm:text-base mt-2 max-w-xl mx-auto">
            Vi står klar til at hjælpe dig med dit næste malerprojekt. Kontakt os i dag for et uforpligtende tilbud.
          </p>
        </div>
      </section>

      {/* Main Contact Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Left Column: Contact Info */}
            <div className="lg:col-span-5 space-y-8">
              <div>
                <h2 className="text-2xl sm:text-3xl font-black text-gray-900 mb-4">
                  Kontaktoplysninger
                </h2>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Hos Malerfirma Villy Poulsen ApS besvarer vi gerne dine spørgsmål. Du er altid velkommen til at kontakte os via telefon, e-mail eller ved at udfylde formularen.
                </p>
              </div>

              {/* Contact Cards */}
              <div className="space-y-4">
                {/* Telefon */}
                <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100 flex gap-4">
                  <div className="p-3 rounded-xl bg-[#01529E]/5 text-[#01529E] shrink-0 h-12 w-12 flex items-center justify-center">
                    <Phone className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 text-xs uppercase tracking-wider">Ring til os</h3>
                    <p className="text-gray-500 text-xs mt-0.5">Direkte telefon og mobil</p>
                    <a
                      href="tel:40637670"
                      className="text-[#01529E] font-bold text-lg hover:text-[#01529E]/80 block mt-2 transition-colors whitespace-nowrap"
                    >
                      Telefon: 40 63 76 70
                    </a>
                  </div>
                </div>

                {/* E-mail */}
                <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100 flex gap-4">
                  <div className="p-3 rounded-xl bg-[#01529E]/5 text-[#01529E] shrink-0 h-12 w-12 flex items-center justify-center">
                    <Mail className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 text-xs uppercase tracking-wider">Skriv til os</h3>
                    <p className="text-gray-500 text-xs mt-0.5">Vi besvarer e-mails hurtigst muligt</p>
                    <a
                      href="mailto:mail@malerfirma-villy-poulsen.dk"
                      className="text-[#01529E] font-bold text-base hover:text-[#01529E]/80 block mt-2 transition-colors break-all"
                    >
                      mail@malerfirma-villy-poulsen.dk
                    </a>
                  </div>
                </div>

                {/* General Info */}
                <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100 space-y-4 text-sm">
                  <div className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-[#ED1C26] shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-bold text-gray-900 text-xs uppercase tracking-wider">Adresse</h4>
                      <p className="text-gray-600 mt-1">Ydunsvej 5A, DK-8981 Spentrup</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-[#ED1C26] shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-bold text-gray-900 text-xs uppercase tracking-wider">Område</h4>
                      <p className="text-gray-600 mt-1">Vi udfører malerarbejde i hele Østjylland</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <ShieldCheck className="h-5 w-5 text-[#ED1C26] shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-bold text-gray-900 text-xs uppercase tracking-wider">Virksomhed</h4>
                      <p className="text-gray-600 mt-1">CVR nr.: 10082021</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Contact Form */}
            <div className="lg:col-span-7">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Map Section */}
      <section className="py-12 bg-gray-50 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative rounded-2xl overflow-hidden shadow-xl border border-gray-200 h-[450px] bg-gray-100">
            <div className="absolute inset-0 bg-[#20252C] flex flex-col items-center justify-center p-8 text-center text-white">
              <MapPin className="h-12 w-12 text-[#ED1C26] mb-4 animate-bounce" />
              <h3 className="text-2xl font-bold mb-2">Malerfirma Villy Poulsen ApS</h3>
              <p className="text-gray-400 text-sm max-w-md mb-6">
                Ydunsvej 5A, 8981 Spentrup. Vi dækker hele Østjylland med professionelt malerarbejde.
              </p>
              <a
                href="https://maps.google.com/?q=Ydunsvej+5A,+8981+Spentrup"
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 py-3 rounded-xl bg-[#01529E] hover:bg-[#01529E]/90 text-white text-sm font-bold transition-colors"
              >
                Åben i Google Maps
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
